import {type Dispatch, type SetStateAction} from "react";
import type {SourceState} from "../Sources.tsx";
import {type AudioPlayer} from "./AudioPlayer";
import Ramp from "./ramp/Ramp";
import {ensureSilentAudioKeeper, isSafari} from "./SafariPlayerHelper";

export default class WebApiPlayer implements AudioPlayer {

    private readonly url: string;
    private readonly setState: Dispatch<SetStateAction<SourceState>>
    private context: AudioContext;
    private gain?: GainNode;
    private panner?: StereoPannerNode;
    private source?: AudioBufferSourceNode;
    private buffer?: AudioBuffer;
    private looping = false;
    private graphInitialized = false;

    private readonly ramp: Ramp;
    private volumeSetting = 0;
    playing = false;
    private startTime = 0;
    private pauseTime = 0;

    constructor(context: AudioContext, url: string, setState: Dispatch<SetStateAction<SourceState>>) {
        this.url = url;
        this.setState = setState;
        this.context = context;

        this.ramp = new Ramp((volume) => {
            //console.log("ramp volume to " + volume.toFixed(3) + " @" + this.context.currentTime.toFixed(3) );
            if (this.gain) this.gain.gain.setValueAtTime(volume, this.context.currentTime);
        });
    }

    // Create audio graph only after AudioContext is running (iOS requirement)
    private ensureAudioGraph() {
        if (this.graphInitialized) return;

        this.gain = this.context.createGain();
        this.panner = this.context.createStereoPanner();
        this.gain.connect(this.panner);
        this.panner.connect(this.context.destination);

        this.graphInitialized = true;
    }

    load(loaded: (success: boolean) => void) {
        this.setState("loading");
        fetch(this.url)
            .then(response => response.arrayBuffer())
            .then(arrayBuffer => this.context.decodeAudioData(arrayBuffer))
            .then(async buffer => {
                if (!buffer) throw new Error("No audio buffer generated");
                this.buffer = buffer;
                this.setState("ready");
                loaded(true);
            })
            .catch(error => {
                console.error("Audio loading error: ", error);
                this.setState("error");
                loaded(false);
            });
    }

    loop() {
        this.looping = true;
        //console.log("looping");
    }

    toggle() {
        //console.trace("web api player - toggle playback");
        if (this.playing) {
            this.pause();
            return;
        }

        // iOS: start silent audio keeper to keep audio path open (must be in user gesture)
        if (isSafari()) {
            ensureSilentAudioKeeper();
        }

        const ctxState = this.context.state;
        if (ctxState === "running") {
            this.play();
        } else {
            // Trigger resume synchronously within user gesture
            this.context.resume().catch((error) => {
                console.error("Audio context resume error: ", error);
                this.setState("error");
            });

            // Trigger play synchronously within user gesture
            // Poll with requestAnimationFrame until AudioContext is running
            const waitForRunning = (attempts: number) => {
                if (this.context.state === "running") {
                    console.log("audio context was " + ctxState + ", now " + this.context.state + " after " + attempts + " frames");
                    this.play();
                } else if (attempts < 60) { // ~1 second at 60fps
                    requestAnimationFrame(() => waitForRunning(attempts + 1));
                } else {
                    console.error("AudioContext still not running after " + attempts + " frames");
                    this.setState("error");
                }
            };
            requestAnimationFrame(() => waitForRunning(1));
        }
    }

    volume = (dB: number) => {
        //console.debug("fetch: Attenuate by " + dB.toFixed(1) + " dB.");
        const value = Math.pow(10, (dB / 20));
        // change the volume of audio only during playback and not triggered by pure playback state changes
        if (this.playing && this.volumeSetting !== value && this.gain) {
            this.gain.gain.setValueAtTime(value, this.context.currentTime);
        }
        // this is what the user sees
        this.volumeSetting = value;
    }

    // vertical panning is not supported by StereoPannerNode
    pan = (horizontal: number) => {
        // console.trace("pan horizontal to " + horizontal.toFixed(2));
        if (this.panner) this.panner.pan.value = horizontal;
    }

    dispose() {
        // think about aborting ramp or allowing it to fade out

        // stop source gracefully
        if (this.source) {
            try {
                this.source.onended = null;
                this.source.disconnect();
            } catch {
            }
            try {
                this.source.stop(0);
            } catch {
            }
            this.source = undefined;
        }
    }

    private createAudioSource() {
        const src = this.context.createBufferSource();
        src.buffer = this.buffer!;
        if (this.looping) src.loop = true;

        src.onended = () => {
            //console.trace("fetch ended");
            this.playing = false;
            this.source = undefined;

            if (this.context.currentTime - this.startTime >= this.buffer!.duration) {
                this.pauseTime = 0;
            }
            this.setState("ready");
        };
        return src;
    }

    private play() {
        if (!this.buffer) return;

        // Initialize audio graph on first play (iOS requirement)
        this.ensureAudioGraph();

        this.source = this.createAudioSource();
        this.source.connect(this.gain!);

        const offset = this.pauseTime;
        this.startTime = this.context.currentTime - offset;
        this.source!.start(0, offset);

        this.ramp.on(this.volumeSetting).then(() => {
            // ramp complete
        });
        this.playing = true;
        this.setState("running");
    }

    private pause() {

        this.pauseTime = this.context.currentTime - this.startTime;

        this.ramp.off(this.volumeSetting).then(() => {
            if (!this.source) return;

            this.source.stop();
            this.source.disconnect();
            this.source = undefined;
            this.playing = false;
            this.setState("ready");
        });
    }
}